import {
  CheckBox as DriverApprovedIcon,
  CheckBoxOutlineBlank as DriverNotApprovedIcon,
  PhoneForwarded as PhoneForwardedIcon,
} from "@mui/icons-material";
import { memo, useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PIC, SVG_MUI } from "@ryde/assets";
import {
  FramerTooltip,
  RideFeedbacksCount,
  RideJourneyIndicator,
} from "@ryde/controls";
import { useDropdownItemsCtxProvider } from "@ryde/redux";
import {
  callToClickHandler,
  countStationRouteRealStations,
  currencySignSelector,
  distanceFormula,
  formatPhoneLocal,
  NO_VALUE_DISPLAY,
  RideDriverApprovalEnum,
  useFormats,
  type IRideTableDto,
} from "@ryde/shared";

import {
  KanbanFooterBlock,
  KanbanFooterPrice,
  KanbanItemFooterLine1,
  KanbanItemFooterLine2,
  KanbanItemFooterLine3,
} from "../rides.kanban.item.styled";

interface IFlightNumbers {
  viableFlightView: string;
  allFlights: string[];
}

interface IRideKanbanItemFooter {
  dto: IRideTableDto;
}

export const RideKanbanItemFooter: FC<IRideKanbanItemFooter> = memo(
  ({ dto }) => {
    const [flightNumbers, setFlightNumbers] = useState<IFlightNumbers | null>(
      null,
    );

    const { formatTimeNormalized } = useFormats();
    const currencySign = useRecoilValue(currencySignSelector);
    const { getDropdownLabels } = useDropdownItemsCtxProvider();
    const { t } = useTranslation();

    const showSeatsCountByDriver =
      dto.metadata?.seatsCountByDriver !== undefined;

    const stationsText = t("ride.kanban.station", {
      count: countStationRouteRealStations(dto.metadata?.stationsInfo),
    });

    const productionTooltipContent = `${t("ride.PRODUCTION_ID")}: ${dto.productionId}`;

    const driverName = dto?.driverId
      ? (dto?.driverName ??
        `${t("ride.gridLabel.driverId")} (#${dto?.driverId})`)
      : dto?.metadata?.driverInfo?.name;
    const driverPhone = dto?.driverId
      ? dto?.driverPhone
      : dto?.metadata?.driverInfo?.phone;

    // Absent means no driver approval warning was ever raised for this ride, so nothing is rendered.
    const driverApproved = dto?.computedData?.driverApproved;
    const isDriverApproved = driverApproved === RideDriverApprovalEnum.approved;
    const driverApprovalText = driverApproved
      ? t(
          isDriverApproved
            ? "ride.kanban.DRIVER_APPROVED"
            : "ride.kanban.DRIVER_NOT_APPROVED_YET",
        )
      : "";

    const loadFlightNumbers = useEvent(
      async (flightIds: number[]): Promise<void> => {
        const visibleFlightViewCount = 2;
        const flightLabels = await getDropdownLabels("flight", flightIds);

        const isHasMoreFlightsThanVisible =
          flightLabels?.length > visibleFlightViewCount;
        const visibleLabels = isHasMoreFlightsThanVisible
          ? flightLabels.slice(0, visibleFlightViewCount)
          : flightLabels;

        setFlightNumbers({
          viableFlightView: `${visibleLabels.join(", ")}${isHasMoreFlightsThanVisible ? "..." : ""}`,
          allFlights: flightLabels,
        });
      },
    );

    useEffect(() => {
      const { flightIds } = dto.metadata ?? {};

      if (flightIds?.length) {
        loadFlightNumbers(flightIds);
      }
    }, [dto.metadata, loadFlightNumbers]);

    return (
      <>
        <KanbanItemFooterLine1>
          <KanbanFooterBlock className="divider">
            <FramerTooltip content={t("ride.gridLabel.totalDistance")}>
              {dto?.metadata?.totalDistance
                ? distanceFormula(dto.metadata.totalDistance) +
                  " " +
                  t(`general.KM`)
                : NO_VALUE_DISPLAY}
            </FramerTooltip>
          </KanbanFooterBlock>
          {Boolean(dto?.productionId) && (
            <KanbanFooterBlock>
              <FramerTooltip content={productionTooltipContent}>
                <>
                  <SVG_MUI.ProductionSVG className="sparkle_svg" />
                  {dto.productionId}
                </>
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          <KanbanFooterBlock>
            <FramerTooltip content={stationsText}>
              {dto.metadata?.stationsInfo?.length || 0}
            </FramerTooltip>
          </KanbanFooterBlock>

          {dto.supplierName && (
            <KanbanFooterBlock>
              <FramerTooltip content={t("ride.kanban.SUPPLIER_NAME")}>
                {dto.supplierName}
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          {!!dto.journeyId && (
            <KanbanFooterBlock>
              <RideJourneyIndicator {...dto} />
            </KanbanFooterBlock>
          )}
          {dto.price && (
            <KanbanFooterPrice>
              <FramerTooltip content={t("ride.gridLabel.price")}>
                {currencySign + dto.price}
              </FramerTooltip>
            </KanbanFooterPrice>
          )}
        </KanbanItemFooterLine1>
        <KanbanItemFooterLine2>
          {dto.vehicleTypeName && (
            <KanbanFooterBlock>
              <FramerTooltip content={t("ride.gridLabel.vehicleTypeId")}>
                {dto.vehicleTypeName}
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          {dto.vehicleName && (
            <KanbanFooterBlock>
              <FramerTooltip content={t("ride.gridLabel.vehicleId")}>
                {dto.vehicleName}
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          {dto.shiftTime && (
            <KanbanFooterBlock>
              <FramerTooltip content={t("ride.gridLabel.shiftTime")}>
                {formatTimeNormalized(dto.shiftTime)}
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          {dto.metadata?.dock && (
            <KanbanFooterBlock>
              <FramerTooltip content={t("ride.gridLabel.dock")}>
                {`${t("ride.gridLabel.dock")} ${String(dto.metadata.dock)}`}
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          {showSeatsCountByDriver && (
            <KanbanFooterBlock>
              <FramerTooltip content={t("ride.gridLabel.seatsCountByDriver")}>
                <span>
                  {t("ride.gridLabel.seatsCountByDriver")}:{" "}
                  {dto.metadata?.seatsCountByDriver ?? 0}
                </span>
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
          <KanbanFooterBlock>
            <RideFeedbacksCount {...dto} shouldTruncateText />
          </KanbanFooterBlock>
        </KanbanItemFooterLine2>
        <KanbanItemFooterLine3>
          {Boolean(driverName || driverPhone) && (
            <KanbanFooterBlock>
              {/* Leads the driver line, as the design draws it. Its own tooltip, so it does not
                  inherit the driver-name one; `computedData.driverApproved` is only sent for the
                  ride's CURRENT driver, so it can never label somebody else's answer. */}
              {Boolean(driverApproved) && (
                <FramerTooltip content={driverApprovalText}>
                  <span role="img" aria-label={driverApprovalText}>
                    {isDriverApproved ? (
                      <DriverApprovedIcon className="driver_approved_svg" />
                    ) : (
                      <DriverNotApprovedIcon className="driver_not_approved_svg" />
                    )}
                  </span>
                </FramerTooltip>
              )}
              <FramerTooltip content={t("ride.gridLabel.driverId")}>
                <>
                  {Boolean(driverPhone?.trim()) && (
                    <PhoneForwardedIcon
                      className="driver_phone_forwarded_icon"
                      onClick={() => callToClickHandler(driverPhone)}
                    />
                  )}
                  <span>{driverName || formatPhoneLocal(driverPhone)}</span>
                </>
              </FramerTooltip>
            </KanbanFooterBlock>
          )}

          {flightNumbers?.viableFlightView && (
            <KanbanFooterBlock>
              <FramerTooltip
                content={`${t("flight.plural")} - ${flightNumbers.allFlights.join(", ")}`}
              >
                <>
                  <PIC.AirPlaneSvg className="airplane_svg" />
                  {flightNumbers.viableFlightView}
                </>
              </FramerTooltip>
            </KanbanFooterBlock>
          )}
        </KanbanItemFooterLine3>
      </>
    );
  },
);
